<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import integrationAPI from 'dashboard/api/wgpt/integrations';

import Button from 'next/button/Button.vue';
import Label from 'next/label/Label.vue';
import ViewWrapper from './ViewWrapper.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const isConnecting = ref(null);

const CONNECT_API = {
  salla: params => integrationAPI.connectSalla(params),
  zid: params => integrationAPI.connectZid(params),
};

const INTEGRATION_STORE_KEYS = {
  salla: 'wgpt_salla',
  zid: 'wgpt_zid',
};

const ALL_STORES = [
  {
    key: 'salla',
    image: '/dashboard/images/integrations/wgpt_salla.png',
    darkImage: '/dashboard/images/integrations/wgpt_salla-dark.png',
    title: t('WGPT_ONBOARDING.INTEGRATION.STORES.SALLA.TITLE'),
    description: t('WGPT_ONBOARDING.INTEGRATION.STORES.SALLA.DESCRIPTION'),
  },
  {
    key: 'zid',
    image: '/dashboard/images/integrations/wgpt_zid.png',
    darkImage: '/dashboard/images/integrations/wgpt_zid-dark.png',
    title: t('WGPT_ONBOARDING.INTEGRATION.STORES.ZID.TITLE'),
    description: t('WGPT_ONBOARDING.INTEGRATION.STORES.ZID.DESCRIPTION'),
  },
];

const integrationRecords = computed(
  () => store.getters['integrations/getAppIntegrations']
);

const connectedStoreKeys = computed(
  () => new Set(integrationRecords.value.filter(i => i.enabled).map(i => i.id))
);

const stores = computed(() =>
  ALL_STORES.map(s => ({
    ...s,
    connected: connectedStoreKeys.value.has(INTEGRATION_STORE_KEYS[s.key]),
  }))
);

const hasConnectedStore = computed(() => stores.value.some(s => s.connected));

const connectStore = async storeKey => {
  try {
    isConnecting.value = storeKey;
    localStorage.setItem('wgpt_onboarding_integration', storeKey);
    const returnTo = router.resolve({
      name: 'wgpt_onboarding_integrations',
      params: { accountId: route.params.accountId },
    }).href;
    const { data } = await CONNECT_API[storeKey]({ return_to: returnTo });
    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    }
  } catch {
    isConnecting.value = null;
    localStorage.removeItem('wgpt_onboarding_integration');
  }
};

const handleIntegrationError = async errorMessage => {
  await router.replace({ query: {} });
  useAlert(t('WGPT_ONBOARDING.INTEGRATION.ERROR', { error: errorMessage }));
};

onMounted(() => {
  localStorage.removeItem('wgpt_onboarding_integration');
  const { error, error_message: errorMessage } = route.query;
  if (error === 'true' && errorMessage) {
    handleIntegrationError(errorMessage);
  }
});
</script>

<template>
  <ViewWrapper>
    <div class="space-y-3">
      <div
        v-for="storeItem in stores"
        :key="storeItem.key"
        class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-2xl p-4"
      >
        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-0"
        >
          <div class="flex h-16 w-16 shrink-0 items-center justify-center">
            <img
              :src="storeItem.image"
              :alt="storeItem.title"
              class="max-w-full rounded-xl border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3"
            />
            <img
              :src="storeItem.darkImage"
              :alt="storeItem.title"
              class="max-w-full rounded-xl border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3"
            />
          </div>
          <div class="flex flex-col justify-center sm:mx-4 flex-1">
            <h3 class="mb-1 text-sm font-medium text-n-slate-12">
              {{ storeItem.title }}
            </h3>
            <p class="text-n-slate-11 text-sm leading-6">
              {{ storeItem.description }}
            </p>
          </div>
          <div class="flex shrink-0 items-center">
            <Label
              v-if="storeItem.connected"
              :label="t('WGPT_ONBOARDING.INTEGRATION.CONNECTED')"
              color="teal"
              compact
            />
            <Button
              v-else
              variant="faded"
              color="blue"
              :label="t('WGPT_ONBOARDING.INTEGRATION.CONNECT')"
              icon="i-lucide-external-link"
              :is-loading="isConnecting === storeItem.key"
              :disabled="!!isConnecting && isConnecting !== storeItem.key"
              @click="connectStore(storeItem.key)"
            />
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <Button
        :label="t('WGPT_ONBOARDING.INTEGRATION.BACK')"
        variant="link"
        color="slate"
        @click="router.push({ name: 'wgpt_onboarding' })"
      />
      <Button
        v-if="hasConnectedStore"
        lg
        :label="t('WGPT_ONBOARDING.INTEGRATION.CONTINUE')"
        color="blue"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
      <Button
        v-else
        :label="t('WGPT_ONBOARDING.INTEGRATION.SKIP')"
        variant="link"
        color="slate"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
    </template>
  </ViewWrapper>
</template>
